import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { Comic } from '@tanko/shared'
import { CloudApiClient } from '../lib/api-client'
import { registerComics, registeredComics, unregisteredComics } from '../lib/catalog-registration'
import { catalogOrderRepaired, markCatalogOrderRepaired } from '../lib/library-preferences'
import { loadCategories } from '../lib/category-service'
import { migrationComplete, previewMigration, runCategoryMigration } from '../lib/category-migration-runner'
import { comicsWithUnpushedReadingState } from '../db/reading-repo'
import { pushComicReadingState, pushTargets, runReadingPush } from '../lib/reading-push'

const api = new CloudApiClient()

/** 遷移的階段。註冊先於分類，因為分類指派要推到雲端就得先有作品目錄項。 */
export type MigrationStage = 'registration' | 'order-repair' | 'categories' | 'assignments'
const stageLabels: Record<MigrationStage, string> = {
  registration: '把作品加入雲端目錄',
  'order-repair': '對回作品的加入時間',
  categories: '把分類搬到雲端目錄',
  assignments: '寫入分類指派',
}

/**
 * 雲端目錄的註冊。
 *
 * 作品加入書庫並完成索引後就註冊，因此雲端清單涵蓋尚未備份的作品。註冊只寫
 * metadata，不需要資料夾權限，也不讀取任何圖片——升級後的既有書庫因此能在
 * 尚未授權任何來源的情況下完成。
 */
export const useCatalogStore = defineStore('catalog', () => {
  const running = ref(false)
  const completed = ref(0)
  const total = ref(0)
  const reason = ref('')
  const stage = ref<MigrationStage>('registration')
  /** 這次工作階段已經失敗過就不再自動重試：失敗成因通常是系統性的（未登入、離線）。 */
  let halted = false
  let scheduled: ReturnType<typeof setTimeout> | undefined

  /**
   * 系統性失敗後的退避。閱讀時每隔幾秒就有一次進度寫入會讓書庫重新發出清單，也就會再
   * 觸發一次補送；沒有退避的話，離線期間會一路重試同一個注定失敗的請求。
   */
  const PUSH_BACKOFF_MS = 60000
  let pushing = false
  let pushBlockedUntil = 0
  let pushScheduled: ReturnType<typeof setTimeout> | undefined

  /**
   * 把裝置上還沒送出去的閱讀狀態補到雲端。
   *
   * 沒有任何待推送紀錄時是兩次索引查詢就結束，因此可以掛在每次清單更新之後；真正要送
   * 東西的情況只發生在升級之後第一次連線、以及推送失敗過的那些作品身上。
   */
  async function pushReadingState(comics: readonly Comic[]): Promise<void> {
    if (pushing || Date.now() < pushBlockedUntil) return
    const waiting = await comicsWithUnpushedReadingState()
    const targets = pushTargets(comics, waiting)
    if (!targets.length) return
    pushing = true
    try {
      const outcome = await runReadingPush(targets, target => pushComicReadingState(target, api))
      if (outcome.reason) pushBlockedUntil = Date.now() + PUSH_BACKOFF_MS
    } catch { pushBlockedUntil = Date.now() + PUSH_BACKOFF_MS }
    finally { pushing = false }
  }

  function schedulePush(comics: readonly Comic[]): void {
    if (pushScheduled) clearTimeout(pushScheduled)
    pushScheduled = setTimeout(() => { pushScheduled = undefined; void pushReadingState(comics) }, 600)
  }

  const pending = computed(() => Math.max(total.value - completed.value, 0))
  const stageLabel = computed(() => stageLabels[stage.value])
  /** 停止時說清楚做到哪裡：階段、已完成本數，以及原因。 */
  const stoppedSummary = computed(() => reason.value
    ? `在「${stageLabel.value}」完成 ${completed.value} 筆後停止：${reason.value}`
    : '')

  /**
   * 依序執行三個階段，每一階段的結果在進入下一階段之前就已經持久化。
   *
   * 因此中斷之後重跑會從第一個未完成的階段接續：已註冊的作品不再出現在待註冊清單，
   * 已搬到雲端的分類會因為同名而判為合併。任一階段失敗就停下並保留原因，不繼續
   * 往下跑——後面的階段依賴前面的結果。
   */
  async function run(): Promise<void> {
    if (running.value) return
    running.value = true
    reason.value = ''
    try {
      stage.value = 'registration'
      const outstanding = await unregisteredComics()
      completed.value = 0
      total.value = outstanding.length
      if (outstanding.length) {
        const outcome = await registerComics(outstanding, api, value => { completed.value = value })
        completed.value = outcome.completed
        if (outcome.reason) { reason.value = outcome.reason; halted = true; return }
      }
      // 剛註冊完成的作品現在有目錄項可以掛了，把先前累積在裝置上的閱讀狀態補上去。
      await pushReadingState(await registeredComics())

      // 一次性：早期註冊把整批的建立時間寫成註冊當下那個「現在」，雲端的時間排序因此
      // 全部平手。重送同一批把它對回本地的加入時間；註冊冪等，不會建立第二筆。
      if (!catalogOrderRepaired()) {
        stage.value = 'order-repair'
        const existing = await registeredComics()
        completed.value = 0
        total.value = existing.length
        if (existing.length) {
          const outcome = await registerComics(existing, api, value => { completed.value = value })
          completed.value = outcome.completed
          if (outcome.reason) { reason.value = outcome.reason; halted = true; return }
        }
        markCatalogOrderRepaired()
      }

      stage.value = 'categories'
      // 比對必須建立在當下取得的雲端清單上：清單為空會讓既有分類被誤判為需要新建。
      const cloudCategories = await loadCategories()
      const plan = await previewMigration(cloudCategories)
      if (!migrationComplete(plan)) {
        // 還有需要新建的分類，交由預覽介面讓使用者確認——合併不可逆，不自動套用。
        return
      }
      stage.value = 'assignments'
      completed.value = 0
      total.value = 0
      const outcome = await runCategoryMigration(plan, api, (value, count) => {
        completed.value = value
        total.value = count
      })
      completed.value = outcome.completed
      if (outcome.reason) { reason.value = outcome.reason; halted = true }
    } catch (cause) {
      reason.value = cause instanceof Error ? cause.message : String(cause)
      halted = true
    } finally { running.value = false }
  }

  /**
   * 資料變動後排程一次註冊；連續變動只跑最後一次。失敗過就要由使用者明確重試。
   *
   * 一次性的建立時間修復也要能被排到：全部作品都已註冊的書庫沒有任何待註冊項，
   * 只看「有沒有未註冊的作品」會讓修復永遠等不到執行。
   */
  function schedule(comics: readonly Comic[]): void {
    // 補送不受註冊的停止狀態影響：已經在目錄裡的作品送得出去，與還沒註冊的那些無關。
    schedulePush(comics)
    if (halted || running.value) return
    const needsRegistration = comics.some(comic => comic.syncState === 'unregistered' && comic.indexed)
    if (!needsRegistration && catalogOrderRepaired()) return
    if (scheduled) clearTimeout(scheduled)
    scheduled = setTimeout(() => { scheduled = undefined; void run() }, 400)
  }

  /** 明確重試：清掉停止狀態後立刻再跑一次，順便解除補送的退避。 */
  async function retry(): Promise<void> {
    halted = false
    pushBlockedUntil = 0
    reason.value = ''
    await run()
  }

  return { running, completed, total, pending, reason, stage, stageLabel, stoppedSummary, run, schedule, retry, pushReadingState }
})
